import React, { useState } from 'react';
import { useLunchOrdersData } from '../hooks/useLunchOrdersData';
import { useCellHighlights } from '../hooks/useCellHighlights';

const HIGHLIGHT_COLORS: Record<string, string> = {
  green: '#bbf7d0',
  red: '#fecaca',
  yellow: '#fef08a',
};

export const LciLunchOrders: React.FC = () => {
  const { sheets, loading, error, fetchedAt, refetch, isAutoRefreshActive } = useLunchOrdersData();
  const { getHighlight, cycleHighlight } = useCellHighlights(sheets);
  const [activeTab, setActiveTab] = useState(0);

  const formatTimestamp = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString();
  };

  // If sheets change and active tab is out of bounds, reset
  const safeTab = activeTab < sheets.length ? activeTab : 0;
  const activeSheet = sheets[safeTab];

  return (
    <div style={{ padding: '24px', maxWidth: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
        <h1 style={{ margin: 0, fontSize: '24px' }}>LCI Lunch Orders</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          {fetchedAt && (
            <span style={{ fontSize: '13px', color: '#666' }}>
              Last updated: {formatTimestamp(fetchedAt)}
            </span>
          )}
          {isAutoRefreshActive && (
            <span style={{
              fontSize: '12px',
              color: '#16a34a',
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '12px',
              padding: '2px 10px',
            }}>
              Auto-refresh active
            </span>
          )}
          <button
            onClick={refetch}
            disabled={loading}
            style={{
              padding: '6px 16px',
              fontSize: '14px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#dc2626',
          marginBottom: '16px',
        }}>
          {error}
        </div>
      )}

      {/* Loading state (initial only) */}
      {loading && sheets.length === 0 && !error && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: '#666' }}>
          Loading sheets...
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && sheets.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: '#666' }}>
          No sheets found.
        </div>
      )}

      {/* Tab bar + table */}
      {sheets.length > 0 && (
        <>
          {/* Tab bar */}
          <div style={{
            display: 'flex',
            overflowX: 'auto',
            borderBottom: '2px solid #e5e7eb',
            marginBottom: '16px',
            gap: '0',
          }}>
            {sheets.map((sheet, i) => (
              <button
                key={sheet.name}
                onClick={() => setActiveTab(i)}
                style={{
                  padding: '8px 20px',
                  fontSize: '14px',
                  fontWeight: safeTab === i ? 600 : 400,
                  color: safeTab === i ? '#2563eb' : '#6b7280',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: safeTab === i ? '2px solid #2563eb' : '2px solid transparent',
                  marginBottom: '-2px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {sheet.name}
              </button>
            ))}
          </div>

          {/* Table */}
          {activeSheet && activeSheet.data.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px',
              }}>
                <thead>
                  <tr>
                    {activeSheet.data[0].map((header, ci) => (
                      <th
                        key={ci}
                        style={{
                          textAlign: 'left',
                          padding: '10px 12px',
                          borderBottom: '2px solid #e5e7eb',
                          backgroundColor: '#f9fafb',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {activeSheet.data.slice(1).map((row, ri) => {
                    const stripeColor = ri % 2 === 0 ? '#fff' : '#f9fafb';
                    return (
                      <tr key={ri}>
                        {activeSheet.data[0].map((_, ci) => {
                          const hl = getHighlight(activeSheet.name, ri, ci);
                          return (
                            <td
                              key={ci}
                              onClick={() => cycleHighlight(activeSheet.name, ri, ci)}
                              style={{
                                padding: '8px 12px',
                                borderBottom: '1px solid #e5e7eb',
                                backgroundColor: hl ? HIGHLIGHT_COLORS[hl] : stripeColor,
                                cursor: 'pointer',
                              }}
                            >
                              {row[ci] ?? ''}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#666' }}>
              This sheet is empty.
            </div>
          )}
        </>
      )}
    </div>
  );
};
